// #42 Bars and Meter: labelled horizontal bars (*Load*, *Workload*, *Expertise*, *Decline reasons*, *Candidates by
// stage*). Each row is the words, the value (or the percentage) and an 8 px track (6 px in `status`) whose fill width
// is --mc-pct and colour --mc-bc (a token; default the blue → purple gradient). The fill grows once on first paint;
// motion is off under capture and reduced motion. The track is decorative: the value is in the text.

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { fullTitle } from "../lib/textOf";
import { useHs, type Hs } from "../lib/hs";
import { vars, type McVarValue } from "../lib/vars";

export interface BarItem {
  label: ReactNode;
  /** 0–100. */
  pct: number;
  /** The value's words or number (default the percentage). */
  value?: ReactNode | undefined;
  /** The fill: a token var (var(--green), var(--gold) …); default the blue → purple gradient. */
  color?: McVarValue | undefined;
  hs?: Hs | undefined;
}

export interface BarsProps {
  items: readonly BarItem[];
  /** Long labels wrap instead of ending in an ellipsis. */
  wrapl?: boolean | undefined;
  /** The thinner 6 px track of a status breakdown. */
  status?: boolean | undefined;
  /** Names the group (a section title's id). */
  labelledBy?: string | undefined;
  className?: string | undefined;
}

const clamp = (pct: number) => Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0));

function BarRow({ item, wrapl }: { item: BarItem; wrapl?: boolean | undefined }) {
  const hot = useHs(item.hs);
  const pct = clamp(item.pct);
  return (
    <div className="mc-bar-r" {...hot}>
      <span className="mc-bl" title={wrapl ? undefined : fullTitle(item.label)}>{item.label}</span>
      <span className="mc-bv">{item.value ?? `${pct}%`}</span>
      <span className="mc-bt" aria-hidden="true">
        <b style={vars({ "--mc-pct": `${pct}%`, "--mc-bc": item.color })} />
      </span>
    </div>
  );
}

export function Bars({ items, wrapl, status, labelledBy, className }: BarsProps) {
  return (
    <div className={cx("mc-bars", wrapl && "mc-bars-wrapl", status && "mc-bars-status", className)} role={labelledBy ? "group" : undefined} aria-labelledby={labelledBy}>
      {items.map((item, index) => <BarRow key={index} item={item} wrapl={wrapl} />)}
    </div>
  );
}

export interface MeterProps extends BarItem {
  status?: boolean | undefined;
  /** A live progress (an upload): role="progressbar" with the value, named by `label` or `labelledBy`.
   *  `indeterminate` (a step with no measure, such as a scan): no value is announced, the full track pulses. */
  progress?: { label?: string | undefined; labelledBy?: string | undefined; indeterminate?: boolean | undefined } | undefined;
  className?: string | undefined;
}

/** One bar (a workload meter, an upload's progress). */
export function Meter({ status, progress, className, ...item }: MeterProps) {
  if (!progress) return <Bars items={[item]} status={status} className={className} />;
  return (
    <div
      role="progressbar"
      aria-label={progress.labelledBy ? undefined : progress.label}
      aria-labelledby={progress.labelledBy}
      aria-valuenow={progress.indeterminate ? undefined : Math.round(clamp(item.pct))}
      aria-valuemin={progress.indeterminate ? undefined : 0}
      aria-valuemax={progress.indeterminate ? undefined : 100}
      aria-busy={progress.indeterminate ? true : undefined}
      className={cx("mc-meter-prog", progress.indeterminate && "mc-meter-ind")}
    >
      <Bars items={[item]} status={status} className={className} />
    </div>
  );
}
